<template>
  <input v-model="displayed" @blur="handleBlur" @keydown="handleKeyDown" />
</template>

<script>
class Flag {
  constructor() {
    this._flags = {};
  }

  create(name) {
    this._flags[name] = true;
    setTimeout(() => {
      delete this._flags[name];
    });
  }

  check(name) {
    return this._flags[name] || false;
  }
}

const isValidNumStr = (() => {
  const allowedChars = [
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "0",
    ".",
    "-",
  ];

  return function ({ numStr, options: { max, min, precision } }) {
    //make sure it's a numstr
    numStr = "" + numStr;

    if (numStr.split("").some((char) => !allowedChars.includes(char))) {
      return false;
    }

    if (numStr.includes("-")) {
      if (min >= 0) {
        return false;
      }

      // if there's more than two minuses or there's one but it isn't the  first char
      if (numStr.lastIndexOf("-") !== 0) {
        return false;
      }
    }

    if (numStr.includes(".")) {
      if (precision == 0) {
        //includes dot and precison = 0
        return false;
      }

      if (numStr.split(".").length - 1 > 2) {
        //more than one dot
        return false;
      }

      const decimalPart = numStr.split(".")[1];

      if (decimalPart.length > precision) {
        //more decimals than allowed precison
        return false;
      }
    }

    const num = +numStr;

    if (num < min) {
      // if ( canBecomeBiggerThan(num, min) ) {
      return false;
      // }
    }

    if (num > max) {
      // if ( canBecomeSmallerThan(num, min) ) {
      return false;
      // }
    }

    return true;
  };
})();

function convertNumStrToNum(numStr) {
  if (!numStr.length || numStr === "." || numStr === "-." || numStr === "-") {
    return 0;
  }

  return +numStr;
}

function getPrecisionFromNum(num) {
  const decimalPart = ("" + num).split(".")[1];
  return decimalPart ? decimalPart.length : 0;
}

function stepUp({ num, step, max }) {
  const precision = Math.max(
    getPrecisionFromNum(num),
    getPrecisionFromNum(step)
  );
  return Math.min(+(num + step).toFixed(precision), max);
}

function stepDown({ num, step, min }) {
  const precision = Math.max(
    getPrecisionFromNum(num),
    getPrecisionFromNum(step)
  );
  return Math.max(+(num - step).toFixed(precision), min);
}

//PASSED TESTS
function toPrecisonStr({ num, precision }) {
  return toPrecision({ num, precision }).toFixed(precision);
}

//PASSED TESTS
function toPrecision({ num, precision }) {
  const [naturalPart, decimalPart] = ("" + num).split(".");

  if (decimalPart) {
    if (precision === 0) {
      return +naturalPart;
    } else {
      return +`${naturalPart}.${decimalPart.substr(0, precision)}`;
    }
  } else {
    return num;
  }
}

// function forceNumToRespectOptions( {  numStr, options: {max, min, precision }}) {
// 	const num = +numStr;

//   if (num < min) {
//     return min;
//   }

//   if (num > max) {
//     return max;
//   }

//   if ( getPrecisionFromNum(num) > precision ) {
//   	return toPrecision({num, precision})
//   }
// }

//PASSED TESTS
function getUnconflictingOptions({ max, min, precision, step, justControls }) {
  let internalOptions = {};

  if (min > max) {
    console.warn(
      "'max' cannot be lower than 'min'. 'max' was converted to Infinity"
    );
    internalOptions.max = Infinity;
  } else {
    internalOptions.max = max;
  }

  // precision/max-min-step inconsistency
  const precisionFromMax = getPrecisionFromNum(max);
  const precisionFromMin = getPrecisionFromNum(min);
  const precisionFromStep = getPrecisionFromNum(step);
  const highestOfOptionsPrecision = Math.max(
    precisionFromMax,
    precisionFromMin,
    precisionFromStep
  );

  if (highestOfOptionsPrecision > precision) {
    console.warn(
      "One or more of 'max', 'min' and 'step' have a precision higher than the set 'precision'. 'precision' was converted to the highest of these."
    );
    internalOptions.precision = highestOfOptionsPrecision;
  } else {
    internalOptions.precision = precision;
  }

  internalOptions = {
    ...internalOptions,
    //keep the rest as they are
    min,
    step,
    justControls,
  };

  return internalOptions;
}

function isNonCharInsertingKey(e) {
  return (
    e.shiftKey ||
    e.altKey ||
    e.ctrlKey ||
    (e.metaKey && !e.ctrlKey) ||
    [8, 9, 13, 35, 36, 37, 39, 46, 45].includes(e.which)
  );
}

export default {
  props: {
    max: {
      type: Number,
      default: Infinity,
      validator: (val) => val !== -Infinity,
    },
    min: {
      type: Number,
      default: -Infinity,
      validator: (val) => val !== Infinity,
    },
    step: {
      type: Number,
      default: 1,
    },
    precision: {
      type: Number,
      default: 0,
      validator: (val) => val >= 0 && val <= 100,
    },
    justControls: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      displayed: "",
      initialValue: undefined,
      flag: new Flag(),
    };
  },

  computed: {
    userOptions() {
      const { max, min, precision, step, justControls } = this;

      return {
        max,
        min,
        precision,
        step,
        justControls,
      };
    },

    internalOptions() {
      return getUnconflictingOptions(this.userOptions);
    },
  },

  watch: {
    displayed(displayed) {
      if (isValidNumStr({ numStr: displayed, options: this.internalOptions })) {
        this.emitValue(convertNumStrToNum(displayed));
      } else {
        //this.displayed = lastDisplayed;
      }
    },

    //INIT AND REACT TO OUTSIDE CHANGES
    value: {
      handler(value) {
        if (!this.flag.check(`internalChange`)) {
          const numStr = "" + value;
          let newNum;

          if (isValidNumStr({ numStr, options: this.internalOptions })) {
            newNum = convertNumStrToNum(numStr);
          } else {
            newNum = this.internalOptions.min;
          }

          this.emitValue(newNum);

          this.displayed = toPrecisonStr({
            num: newNum,
            precision: this.internalOptions.precision,
          });
        }
      },
      immediate: true,
    },
  },

  methods: {
    emitValue(value) {
      this.flag.create(`internalChange`);
      this.$emit("input", value);
    },
    // we're a little limited in the keydown event as we dont know what will become the
    // the value of the input until after keyup.
    // we use this event to handle arrow controls (step up/down) and prevent
    // what we can like second dot if precision == 0 or double dots
    // we're we actually perform the real checks is on the displayed value watch event
    handleKeyDown(e) {
      const { which } = e;
      const { min, max, precision, step, justControls } = this.internalOptions;

      // cmds, shifts, backspace, etc. any key that should do it's default
      // we let it do it's thing
      if (isNonCharInsertingKey(e)) {
        return true;
      }

      if (which === 38 || which === 40) {
        // if Arrows aka controls
        let newNum;

        if (which === 38) {
          //up
          newNum = stepUp({ num: this.value, step, max });
        } else {
          //down
          newNum = stepDown({ num: this.value, step, min });
        }

        this.emitValue(newNum);
        this.displayed = toPrecisonStr({ num: newNum, precision });

        e.preventDefault();
        return false;
      }

      if (justControls) {
        e.preventDefault();
        return false;
      }

      if ([190, 173, 108, 110].includes(which)) {
        //dots
        if (!precision || this.displayed.includes(".")) {
          e.preventDefault();
          return false;
        }
      } else if ([189, 109].includes(which)) {
        // minuses
        if (min >= 0 || this.displayed.indexOf("-") !== -1) {
          e.preventDefault();
          return false;
        }
      } else if (
        !((which >= 48 && which <= 57) || (which >= 96 && which <= 105))
      ) {
        //anything but numbers
        e.preventDefault();
        return false;
      }
    },
    handleBlur() {
      this.displayed = toPrecisonStr({
        num: this.value,
        precision: this.precision,
      });
    },
  },
};
</script>

<style scoped>
input {
  font-variant-numeric: tabular-nums lining-nums;
  letter-spacing: -0.4px;
  padding-right: 1px;
}
</style>
